import React from 'react';
import { useState, useEffect } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import {
  ArrowLeft,
  Phone,
  MessageCircle,
  Pencil,
  Trash2,
  User,
  CreditCard,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Send,
  Copy,
  Check,
  Calendar,
  Clock,
} from 'lucide-react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  usePolicyQuery,
  useUpdatePolicyStatusMutation,
  useDeletePolicyMutation,
} from '@features/policies/hooks/usePoliciesQuery.js';
import { formatDate, daysToExpiry, urgencyBucket, daysLabel, initials } from '@repo/utils';
import {
  POLICY_TYPE_LABELS,
  RENEWAL_STATUS_LABELS,
  URGENCY_LABELS,
  WHATSAPP_TEMPLATE,
} from '@repo/constants';
import { RenewalStatus } from '@repo/types';
import type { PolicyWithClient } from '@repo/types';
import PageLoader from '@components/ui/PageLoader.js';
import Dialog from '@components/ui/Dialog.js';
import AlertDialog from '@components/ui/AlertDialog.js';
import Button from '@components/ui/Button.js';
import Input from '@components/ui/Input.js';
import { cn } from '@utils/Cn.js';

const ALL_STATUSES: RenewalStatus[] = [
  RenewalStatus.PENDING,
  RenewalStatus.REMINDED,
  RenewalStatus.RENEWED,
  RenewalStatus.NOT_RENEWED,
  RenewalStatus.LAPSED,
];

const statusMeta: Record<
  RenewalStatus,
  { dot: string; badge: 'pending' | 'reminded' | 'renewed' | 'notRenewed' | 'lapsed' }
> = {
  [RenewalStatus.PENDING]: { dot: 'bg-amber-edge', badge: 'pending' },
  [RenewalStatus.REMINDED]: { dot: 'bg-sky-400', badge: 'reminded' },
  [RenewalStatus.RENEWED]: { dot: 'bg-green-edge', badge: 'renewed' },
  [RenewalStatus.NOT_RENEWED]: { dot: 'bg-red-edge', badge: 'notRenewed' },
  [RenewalStatus.LAPSED]: { dot: 'bg-gray-edge', badge: 'lapsed' },
};

function resolveTemplate(
  template: string,
  vals: Record<string, string | number | null | undefined>,
): string {
  function s(v: string | number | null | undefined): string {
    if (v == null) return '';
    return String(v);
  }
  return template
    .replace(/\{\{insuredName\}\}/g, s(vals.insuredName))
    .replace(/\{\{endDate\}\}/g, s(vals.endDate))
    .replace(/\{\{policyType\}\}/g, s(vals.policyType))
    .replace(/\{\{vehicleNumber\}\}/g, s(vals.vehicleNumber))
    .replace(/\{\{premiumPrice\}\}/g, vals.premiumPrice ? `Rs.${String(vals.premiumPrice)}` : '')
    .replace(/\{\{paymentLink\}\}/g, s(vals.paymentLink))
    .replace(/\{\{renewalNotice\}\}/g, s(vals.renewalNotice))
    .replace(/\{\{#vehicleNumber\}\}(.*?)\{\{\/vehicleNumber\}\}/g, vals.vehicleNumber ? '$1' : '')
    .replace(/\{\{#premiumPrice\}\}(.*?)\{\{\/premiumPrice\}\}/g, vals.premiumPrice ? '$1' : '')
    .replace(/\{\{#paymentLink\}\}(.*?)\{\{\/paymentLink\}\}/g, vals.paymentLink ? '$1' : '')
    .replace(/\{\{#renewalNotice\}\}(.*?)\{\{\/renewalNotice\}\}/g, vals.renewalNotice ? '$1' : '')
    .replace(/\s+/g, ' ')
    .trim();
}

export default function PolicyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = usePolicyQuery(id);
  const policy = data?.data;

  if (isLoading || !policy) return <PageLoader message="Loading policy detail cockpit" />;

  return <PolicyDetailContent policy={policy} id={id} />;
}

function PolicyDetailContent({ policy, id }: { policy: PolicyWithClient; id: string }) {
  const history = useHistory();
  const location = useLocation();
  const updateStatus = useUpdatePolicyStatusMutation();
  const deletePolicy = useDeletePolicyMutation();

  const [showWAModal, setShowWAModal] = useState(false);
  const [premiumPrice, setPremiumPrice] = useState('');
  const [paymentLink, setPaymentLink] = useState('');
  const [renewalNotice, setRenewalNotice] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copied, setCopied] = useState(false);

  function handleCopyLink() {
    if (!policy.paymentLink) return;
    void navigator.clipboard.writeText(policy.paymentLink);
    setCopied(true);
    toast.success('Payment link copied to clipboard');
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  }

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('wa') === 'true') {
      setPremiumPrice(String(policy.premiumPrice ?? ''));
      setPaymentLink(policy.paymentLink ?? '');
      setRenewalNotice(policy.renewalNotice ?? '');
      setShowWAModal(true);
      history.replace(`/policies/${id}`);
    }
  }, [location.search, policy, id, history]);

  const days = daysToExpiry(policy.endDate);
  const urgency = urgencyBucket(days);
  const tel = policy.client.mobileNumber;

  function callAgent() {
    if (!tel) {
      toast.error('No phone number on file');
      return;
    }
    window.open(`tel:${tel}`);
  }

  function smsAgent() {
    if (!tel) {
      toast.error('No phone number on file');
      return;
    }
    const body = buildMessage();
    window.open(`sms:${tel}?body=${encodeURIComponent(body)}`, '_blank');
  }

  function buildMessage() {
    return resolveTemplate(WHATSAPP_TEMPLATE, {
      insuredName: policy.client.insuredName,
      endDate: formatDate(policy.endDate),
      policyType: POLICY_TYPE_LABELS[policy.policyType] ?? policy.policyType,
      vehicleNumber: policy.vehicleNumber,
      premiumPrice: premiumPrice || policy.premiumPrice,
      paymentLink: paymentLink || policy.paymentLink,
      renewalNotice: renewalNotice || policy.renewalNotice,
    });
  }

  function sendWhatsApp() {
    if (!tel) {
      toast.error('No phone number on file');
      return;
    }
    window.open(
      `https://wa.me/${tel.replace('+', '')}?text=${encodeURIComponent(buildMessage())}`,
      '_blank',
    );
    setShowWAModal(false);
  }

  function handleStatusChange(s: RenewalStatus) {
    updateStatus.mutate({ id, status: s });
  }

  function handleDelete() {
    deletePolicy.mutate(id, {
      onSuccess: () => {
        toast.success('Policy deleted successfully');
        history.replace('/policies');
      },
    });
  }

  const isDeleting = deletePolicy.isPending;
  const messagePreview = buildMessage();

  const urgencyBannerClass = {
    overdue: 'border-rose-500/15 bg-rose-500/[0.04]',
    due7: 'border-amber-500/15 bg-amber-500/[0.04]',
    due30: 'border-emerald-500/15 bg-emerald-500/[0.04]',
    future: 'border-slate-500/15 bg-slate-500/[0.04]',
  }[urgency];

  const urgencyGradient = {
    overdue: 'from-rose-500/10 via-rose-500/[0.02] to-transparent',
    due7: 'from-amber-500/10 via-amber-500/[0.02] to-transparent',
    due30: 'from-emerald-500/10 via-emerald-500/[0.02] to-transparent',
    future: 'from-slate-500/10 via-slate-500/[0.02] to-transparent',
  }[urgency];

  const avatarGradient = {
    overdue: 'from-rose-500 to-red-600 text-white shadow-rose-500/20',
    due7: 'from-amber-500 to-yellow-600 text-white shadow-amber-500/20',
    due30: 'from-emerald-500 to-teal-600 text-white shadow-emerald-500/20',
    future: 'from-slate-500 to-slate-600 text-white shadow-slate-500/20',
  }[urgency];

  const badgeStyle = {
    overdue:
      'bg-rose-500/10 text-rose-700 border-rose-500/20 dark:text-rose-300 dark:bg-rose-950/40 dark:border-rose-900/50',
    due7: 'bg-amber-500/10 text-amber-700 border-amber-500/20 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-900/50',
    due30:
      'bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-900/50',
    future:
      'bg-slate-500/10 text-slate-700 border-slate-500/20 dark:text-slate-300 dark:bg-slate-950/40 dark:border-slate-900/50',
  }[urgency];

  const ambientGlowColor = {
    overdue: 'bg-rose-500/10',
    due7: 'bg-amber-500/10',
    due30: 'bg-emerald-500/10',
    future: 'bg-slate-500/10',
  }[urgency];

  const StatusIcon = {
    overdue: AlertCircle,
    due7: Clock,
    due30: CheckCircle2,
    future: CheckCircle2,
  }[urgency];

  return (
    <IonPage>
      <IonContent className="ion-no-padding">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          className="min-h-full bg-body-bg"
        >
          {/* ── Hero urgency banner ── */}
          <div
            className={cn(
              'border-b bg-gradient-to-b px-4 sm:px-6 lg:px-8 pt-5 pb-6 relative overflow-hidden backdrop-blur-md',
              urgencyBannerClass,
              urgencyGradient,
            )}
          >
            {/* Ambient Background Light Glow */}
            <div
              className={cn(
                'absolute -top-12 -left-12 w-64 h-64 rounded-full blur-3xl opacity-60 pointer-events-none',
                ambientGlowColor,
              )}
            />

            <div className="max-w-6xl mx-auto relative z-10">
              {/* Breadcrumb — desktop */}
              <nav className="hidden md:flex items-center gap-1.5 text-[11px] font-semibold text-ink-faint opacity-70 mb-3">
                <button
                  type="button"
                  onClick={() => {
                    history.push('/policies');
                  }}
                  className="hover:text-slate transition-colors cursor-pointer"
                >
                  Renewals
                </button>
                <span>›</span>
                <span className="text-ink font-bold">{policy.client.insuredName}</span>
              </nav>

              {/* Mobile-only Top Action Bar */}
              <div className="flex md:hidden items-center justify-between w-full mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1.5"
                  onClick={() => {
                    history.goBack();
                  }}
                >
                  <ArrowLeft size={14} />
                  <span>Back</span>
                </Button>

                <div className="flex gap-2">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex items-center gap-1.5 h-9 px-3 rounded-xl border border-line bg-surface text-ink text-xs font-bold cursor-pointer shadow-sm transition-all"
                    onClick={() => {
                      history.push(`/policies/${id}/edit`, { from: location.pathname });
                    }}
                    aria-label="Edit policy"
                  >
                    <Pencil size={12} className="text-slate" />
                    <span>Edit</span>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex items-center gap-1.5 h-9 px-3 rounded-xl border border-red-edge/20 bg-red-bg/85 text-red-fg text-xs font-bold cursor-pointer shadow-sm transition-all"
                    onClick={() => {
                      setConfirmDelete(true);
                    }}
                    aria-label="Delete policy"
                  >
                    <Trash2 size={12} />
                    <span>Delete</span>
                  </motion.button>
                </div>
              </div>

              {/* Main Header Layout */}
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex items-start gap-3 sm:gap-4.5 min-w-0">
                  {/* Desktop-only Back button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="!hidden md:!flex items-center gap-1.5"
                    onClick={() => {
                      history.goBack();
                    }}
                  >
                    <ArrowLeft size={14} />
                    <span>Back</span>
                  </Button>

                  {/* Avatar + Title Container */}
                  <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                    <motion.div
                      whileHover={{ scale: 1.03 }}
                      className={cn(
                        'flex h-13 w-13 sm:h-15 sm:w-15 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr font-black text-base sm:text-lg shadow-md select-none border border-white/20 transition-all mt-0.5',
                        avatarGradient,
                      )}
                    >
                      {initials(policy.client.insuredName)}
                    </motion.div>

                    <div className="text-left min-w-0">
                      <h2 className="text-xl sm:text-2xl lg:text-3xl font-black text-ink leading-tight tracking-tight break-words">
                        {policy.client.insuredName}
                      </h2>

                      {/* Structured Info Badges */}
                      <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                        {/* Urgency Status Badge */}
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold border backdrop-blur-sm shadow-sm',
                            badgeStyle,
                          )}
                        >
                          <StatusIcon size={12} className="shrink-0" />
                          {URGENCY_LABELS[urgency]}
                        </span>

                        {/* Time Left Badge */}
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold border border-line bg-surface/50 text-ink-soft backdrop-blur-sm shadow-sm">
                          <Clock size={12} className="shrink-0" />
                          {daysLabel(days)}
                        </span>

                        {/* Expiry Date Badge */}
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold border border-line bg-surface/50 text-ink-soft backdrop-blur-sm shadow-sm">
                          <Calendar size={12} className="shrink-0" />
                          Expires {formatDate(policy.endDate)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop-only Action buttons */}
                <div className="hidden md:flex gap-2 shrink-0 justify-end">
                  <motion.button
                    whileHover={{ scale: 1.02, translateY: -1 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex items-center gap-1.5 h-10 px-4 rounded-xl border border-line bg-surface text-ink hover:text-slate hover:border-slate/30 text-xs font-bold cursor-pointer shadow-sm hover:shadow transition-all"
                    onClick={() => {
                      history.push(`/policies/${id}/edit`, { from: location.pathname });
                    }}
                    aria-label="Edit policy"
                  >
                    <Pencil size={13} className="text-slate" />
                    <span>Edit Details</span>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02, translateY: -1 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex items-center gap-1.5 h-10 px-4 rounded-xl border border-red-edge/20 bg-red-bg/85 text-red-fg hover:bg-red-bg hover:border-red-edge/40 text-xs font-bold cursor-pointer shadow-sm hover:shadow transition-all"
                    onClick={() => {
                      setConfirmDelete(true);
                    }}
                    aria-label="Delete policy"
                  >
                    <Trash2 size={13} />
                    <span>Delete</span>
                  </motion.button>
                </div>
              </div>

              {/* Missing contact warning */}
              {policy.client.missingContact && (
                <div className="mt-4 flex items-start sm:items-center gap-2.5 text-xs font-bold bg-rose-500/[0.06] text-rose-800 border border-rose-500/20 rounded-xl px-4 py-3 shadow-sm">
                  <AlertCircle size={15} className="shrink-0 text-rose-600 mt-0.5 sm:mt-0" />
                  <span>
                    Missing mobile number — phone, SMS, and WhatsApp actions are currently disabled.
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ── Content body ── */}
          <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-10 py-5 pb-28 md:pb-10">
            {/* ── Three-column layout on desktop, two on tablet, one on mobile ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 items-start lg:items-stretch">
              {/* ─── Col 1: Client card ─── */}
              <section className="flex flex-col bg-surface border border-line rounded-[20px] sm:rounded-[24px] p-5 sm:p-6 lg:p-7 shadow-[0_4px_24px_rgba(15,23,42,0.06)] text-left">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-950/45 text-indigo-700 dark:text-indigo-300 border border-indigo-200/40 shrink-0">
                    <User size={15} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-ink leading-tight">Client Details</h3>
                    <p className="text-[10px] text-ink-faint">Insurance policy client registry</p>
                  </div>
                </div>

                <div className="space-y-0 divide-y divide-line">
                  <DataRow label="Insured Name" value={policy.client.insuredName} />
                  {policy.referenceNote && (
                    <DataRow
                      label="Reference Label"
                      value={
                        <span className="rounded-lg bg-paper px-2 py-0.5 border border-line text-xs font-bold">
                          {policy.referenceNote}
                        </span>
                      }
                    />
                  )}
                  <DataRow
                    label="Mobile"
                    value={
                      <span className="font-mono">{policy.client.mobileNumber ?? 'None'}</span>
                    }
                  />
                </div>

                {/* Communication hub — lives at bottom of col 1 on desktop */}
                <div className="mt-4 pt-4 border-t border-line">
                  <h4 className="text-xs font-bold text-ink-faint uppercase tracking-wider mb-3">
                    Contact Actions
                  </h4>
                  {/* Mobile: 3-grid vertical; desktop: horizontal row */}
                  <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
                    <CommButton
                      icon={<Phone size={16} />}
                      label="Call"
                      disabled={!tel}
                      className="text-blue-700 bg-blue-50 border-blue-200 dark:text-blue-300 dark:bg-blue-950/40 dark:border-blue-900/50"
                      onClick={callAgent}
                    />
                    <CommButton
                      icon={<MessageCircle size={16} />}
                      label="WhatsApp"
                      disabled={!tel}
                      className="text-green-700 bg-green-50 border-green-200 dark:text-green-300 dark:bg-green-950/40 dark:border-green-900/50"
                      onClick={() => {
                        setPremiumPrice(String(policy.premiumPrice ?? ''));
                        setPaymentLink(policy.paymentLink ?? '');
                        setRenewalNotice(policy.renewalNotice ?? '');
                        setShowWAModal(true);
                      }}
                    />
                    <CommButton
                      icon={<Send size={16} />}
                      label="SMS"
                      disabled={!tel}
                      className="text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-900/50"
                      onClick={smsAgent}
                    />
                  </div>
                </div>
              </section>

              {/* ─── Col 2: Policy Info ─── */}
              <section className="flex flex-col bg-surface border border-line rounded-[20px] sm:rounded-[24px] p-5 sm:p-6 lg:p-7 shadow-[0_4px_24px_rgba(15,23,42,0.06)] text-left">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate/10 dark:bg-slate/20 text-slate border border-slate/15 shrink-0">
                    <CreditCard size={15} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-ink leading-tight">Policy Information</h3>
                    <p className="text-[10px] text-ink-faint">Renewal registry data</p>
                  </div>
                </div>

                <div className="space-y-0 divide-y divide-line">
                  <DataRow
                    label="Insurance Type"
                    value={
                      <span className="rounded-lg bg-paper px-2 py-0.5 border border-line text-xs font-bold">
                        {POLICY_TYPE_LABELS[policy.policyType] ?? policy.policyType}
                      </span>
                    }
                  />
                  {policy.vehicleNumber && (
                    <DataRow
                      label="Vehicle No."
                      value={
                        <span className="font-mono uppercase text-xs bg-paper px-2 py-0.5 rounded-lg border border-line font-bold">
                          {policy.vehicleNumber}
                        </span>
                      }
                    />
                  )}
                  <DataRow
                    label="Policy No."
                    value={
                      <span className="font-mono text-xs">{policy.policyNumber ?? 'None'}</span>
                    }
                  />
                  <DataRow
                    label="Renewal Expiry"
                    value={<span className="font-semibold">{formatDate(policy.endDate)}</span>}
                  />
                  {policy.premiumPrice != null && (
                    <DataRow
                      label="Premium"
                      value={
                        <span className="text-sm font-black text-ink">
                          ₹{policy.premiumPrice.toLocaleString('en-IN')}
                        </span>
                      }
                    />
                  )}
                  {policy.paymentLink && (
                    <DataRow
                      label="Payment Link"
                      value={
                        <div className="flex items-center gap-1.5 justify-end min-w-0 max-w-full">
                          <a
                            href={policy.paymentLink}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1 text-slate hover:underline font-mono text-[11px] min-w-0"
                            title={policy.paymentLink}
                          >
                            <ExternalLink size={11} className="shrink-0" />
                            <span className="truncate block max-w-[100px] sm:max-w-[140px]">
                              {policy.paymentLink}
                            </span>
                          </a>
                          <button
                            onClick={handleCopyLink}
                            className="p-1 rounded bg-paper border border-line text-ink-soft hover:text-ink hover:bg-line-strong/30 active:scale-90 transition-all cursor-pointer shrink-0"
                            title="Copy link"
                          >
                            {copied ? (
                              <Check size={11} className="text-green-fg" />
                            ) : (
                              <Copy size={11} />
                            )}
                          </button>
                        </div>
                      }
                    />
                  )}
                  {policy.renewalNotice && (
                    <DataRow
                      label="Notice"
                      value={
                        <span className="text-right max-w-[55%] leading-relaxed text-ink-soft text-xs">
                          {policy.renewalNotice}
                        </span>
                      }
                    />
                  )}
                </div>
              </section>

              {/* ─── Col 3: Status Switcher ─── */}
              {/* On mobile/tablet: spans 1 col (stacks). On lg: dedicated 3rd col */}
              <section className="flex flex-col bg-surface border border-line rounded-[20px] sm:rounded-[24px] p-5 sm:p-6 lg:p-7 shadow-[0_4px_24px_rgba(15,23,42,0.06)] text-left md:col-span-2 lg:col-span-1">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-1.5 h-4 bg-slate rounded-full" />
                  <h3 className="text-sm font-bold text-ink">Manage Renewal Status</h3>
                </div>

                {/* On tablet (col-span-2): 2-column grid of status buttons */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-2 auto-rows-fr">
                  {ALL_STATUSES.map((s) => {
                    const active = policy.renewalStatus === s;
                    const meta = statusMeta[s];
                    return (
                      <motion.button
                        key={s}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          handleStatusChange(s);
                        }}
                        className={cn(
                          'relative flex items-center gap-3 rounded-xl border px-4 py-3 text-sm font-semibold transition-all cursor-pointer text-left',
                          active
                            ? 'border-slate bg-slate/8 dark:bg-slate/12 text-ink shadow-sm'
                            : 'border-line bg-paper/60 text-ink-soft hover:bg-paper hover:border-line-strong hover:text-ink',
                        )}
                      >
                        <span
                          className={cn(
                            'w-2 h-2 rounded-full shrink-0 transition-all',
                            active ? meta.dot : 'bg-line-strong',
                          )}
                        />
                        <span className="flex-1">{RENEWAL_STATUS_LABELS[s] ?? s}</span>
                        {active && (
                          <motion.span
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="shrink-0"
                          >
                            <CheckCircle2 size={15} className="text-slate" />
                          </motion.span>
                        )}
                        {active && (
                          <motion.span
                            layoutId="status-active-bar"
                            className="absolute left-0 top-3.5 bottom-3.5 w-1 rounded-r-full bg-slate"
                            initial={{ opacity: 0, scaleY: 0.5 }}
                            animate={{ opacity: 1, scaleY: 1 }}
                            exit={{ opacity: 0, scaleY: 0.5 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                          />
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </section>
            </div>
          </div>
        </motion.div>
      </IonContent>

      {/* ── WhatsApp Composer Dialog ── */}
      <Dialog
        open={showWAModal}
        onClose={() => {
          setShowWAModal(false);
        }}
        title="WhatsApp Composer"
        description="Customize the message before sending."
        sheet
      >
        <div className="space-y-4">
          <Input
            label="Premium Price (Rs.)"
            placeholder="e.g. 12500"
            type="number"
            value={premiumPrice}
            onChange={(e) => {
              setPremiumPrice(e.target.value);
            }}
          />
          <Input
            label="Payment Link"
            placeholder="https://pay.insurer.com/..."
            value={paymentLink}
            onChange={(e) => {
              setPaymentLink(e.target.value);
            }}
          />
          <Input
            label="Renewal Notice Note"
            placeholder="e.g. NCB applicable"
            value={renewalNotice}
            onChange={(e) => {
              setRenewalNotice(e.target.value);
            }}
          />

          <div className="rounded-xl border border-green-edge/25 bg-green-bg p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider mb-2 text-green-fg">
              Live Preview
            </p>
            <p className="text-xs leading-relaxed text-green-fg font-medium">{messagePreview}</p>
          </div>

          <Button
            variant="primary"
            size="lg"
            className="w-full bg-[#25D366] hover:bg-[#1ea855] shadow-[0_4px_16px_rgba(37,211,102,0.30)] border-transparent"
            leftIcon={<MessageCircle size={16} />}
            onClick={sendWhatsApp}
          >
            Send via WhatsApp
          </Button>
        </div>
      </Dialog>

      {/* ── Delete Confirmation ── */}
      <AlertDialog
        open={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
        }}
        onConfirm={handleDelete}
        title="Delete Policy?"
        description="This will permanently remove the renewal registry card and all records. This action cannot be undone."
        confirmLabel="Yes, Delete"
        cancelLabel="Keep Policy"
        variant="destructive"
        loading={isDeleting}
        icon={<Trash2 size={20} />}
      />
    </IonPage>
  );
}

/* ── Helpers ── */
function DataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center py-2.5 lg:py-3 text-xs lg:text-sm min-w-0 w-full gap-3">
      <span className="font-semibold text-ink-faint shrink-0">{label}</span>
      <div className="font-bold text-ink text-right min-w-0 flex-1 flex justify-end">{value}</div>
    </div>
  );
}

function CommButton({
  icon,
  label,
  onClick,
  disabled,
  className,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  className: string;
}) {
  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex flex-col items-center gap-1.5 py-2.5 sm:py-3 lg:py-4 rounded-xl border font-sans text-[10px] sm:text-[11px] lg:text-xs font-bold shadow-sm transition-all',
        'disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer',
        className,
      )}
    >
      {icon}
      {label}
    </motion.button>
  );
}
